/* eslint-disable max-lines */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useStoreon } from 'storeon/react';
import { Input } from 'fronton-react';
import { Button } from 'common/components/button';
import { DropdownOption, PhoneInput, Select } from 'common/components/inputs';
import { Typography } from 'common/components/typography';
import { LinkWithGAClickButton } from 'common/components/link';
import { SEND_LEAD_REQUEST, LEADS_STORE_NAME } from 'modules/leads';
import { analyticsUtils } from 'utils/analytics-utils';
import { DEFAULT_CHANNEL, DEFAULT_STORE_ID, PRODUCT_RULES, SERVICE_RULES } from './consultation-form-constants';
import {
  form,
  formField,
  linkStyle,
  sendButton,
  captionText,
} from './consultation-form-styles';
import { FormType } from './consultation-form-types';

type FormData = {
  [key in FieldName]?: string;
};

type FieldName = 'name' | 'phone' | 'storeId' | 'comment';

type FocusedFields = {
  [key in FieldName]?: boolean;
};

type ConsultationFormProps = {
  channel?: string;
  commentLabel?: string;
  consultationType: string;
  elementBlock: string;
  elementName: string;
  isCommentVisible?: boolean;
  servicesSale: boolean | undefined;
  shopDropdownList: DropdownOption[];
  subChannel?: string;
  hardcodeComment?: string;
  formType: FormType;
};

/**
 * View компонент формы заявки на консультирование
 */
export const ConsultationForm: React.FC<ConsultationFormProps> = ({
  channel = DEFAULT_CHANNEL,
  commentLabel,
  consultationType,
  elementBlock,
  elementName,
  isCommentVisible,
  servicesSale,
  shopDropdownList,
  subChannel,
  hardcodeComment,
  formType,
}) => {
  const { t } = useTranslation();
  const nameRef = useRef<HTMLInputElement | null>(null);
  const phoneRef = useRef<HTMLInputElement | null>(null);

  const [focusedFields, setFocusedFields] = useState<FocusedFields>({});
  const [formErrors, setFormErrors] = useState<FormData>({});
  const [formData, setFormData] = useState<FormData>({});
  const { leads, dispatch } = useStoreon(LEADS_STORE_NAME);
  const isLeadRequestSent = useMemo(() => leads?.leadRequestStatus === 'Pending', [
    leads?.leadRequestStatus,
  ]);
  const rules = servicesSale ? SERVICE_RULES : PRODUCT_RULES;

  const defaultCommentLabel = useMemo(() => {
    if (isCommentVisible) {
      return t('comment', { defaultValue: 'Комментарий', ns: 'consultation-form' }) || 'Комментарий'
    }

    return '';
  }, [t, isCommentVisible]);

  /**
   * Инициализация формы
   */
  React.useEffect(() => {
    if (shopDropdownList?.length === 0) {
      setFormData({ storeId: DEFAULT_STORE_ID });
    }

    if (shopDropdownList?.length === 1) {
      setFormData({ storeId: shopDropdownList[0].value });
    }
  }, [shopDropdownList]);

  /**
   * Обработчик изменения полей формы(имя, телефон)
   * @param { React.ChangeEvent<HTMLInputElement>} e
   * @param {string} value
   */
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>, value: string) => {
    const { name } = e.target;
    if (formData[name as FieldName] !== value) {
      setFormData({ ...formData, [e.target.name]: value });
    }
  };

  /**
   * Обработчик установки фокуса на поле
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const handleFieldFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const { name } = e.target;
      if (!focusedFields[name as FieldName]) {
        analyticsUtils.sendEvent('GA:focus', {
          elementType: 'focus',
          elementBlock,
          elementText: e.target.name,
          elementName,
        });
        setFocusedFields({ ...focusedFields, [name]: true });
      }
    },
    [elementBlock, elementName, focusedFields, setFocusedFields]
  );

  /**
   * Обработчик изменения поля выбора магазина
   * @param {string | null} value
   */
  const handleShopSelect = (value: string | null) => {
    if (value && formData.storeId !== value) {
      setFormData({ ...formData, storeId: value });
      analyticsUtils.sendEvent('GA:clickButton', {
        elementType: 'button',
        elementBlock,
        elementName,
        elementText: shopDropdownList.find((shop: DropdownOption) => shop.value === value)?.text,
      });
    }
  };

  /**
   * Обработчик отправки формы
   */
  const handleFormSubmit = () => {
    const { comment, storeId, name, phone } = formData;
    if (!name) {
      nameRef.current?.focus();
      return;
    }

    if (!phone) {
      phoneRef.current?.focus();
      return;
    }

    if (!storeId) {
      setFormErrors({ ...formErrors, storeId: t('shop-error', { defaultValue: 'Выберите магазин', ns: 'consultation-form' }) || 'Выберите магазин' });
      return;
    }

    const getFinalComment = () => {
      if (comment && hardcodeComment) {
        return `${comment} - ${hardcodeComment}`;
      }

      if (comment) {
        return comment;
      }
      if (hardcodeComment) {
        return hardcodeComment;
      }
      return undefined;
    };

    dispatch(SEND_LEAD_REQUEST.init, {
      type: consultationType,
      channel,
      subchannel: subChannel,
      storeId: storeId.toString(),
      contact: { name, phone },
      comment: getFinalComment(),
    });
  };

  return (
    <form className={form}>
      <Input
        className={formField}
        label={t('name', { defaultValue: 'Имя', ns: 'consultation-form' }) || 'Имя'}
        name="name"
        onChange={handleFormChange}
        onFocus={handleFieldFocus}
        placeholder={t('name', { defaultValue: 'Имя', ns: 'consultation-form' }) || 'Имя'}
        ref={nameRef}
        value={formData.name}
      />

      <PhoneInput
        className={formField}
        name="phone"
        onChange={handleFormChange}
        label={t('phone', { defaultValue: 'Телефон', ns: 'consultation-form' }) || 'Телефон'}
        onFocus={handleFieldFocus}
        phoneRef={(el: HTMLInputElement | null) => {
          phoneRef.current = el;
        }}
        value={formData.phone}
      />

      {shopDropdownList.length > 1 && (
        <Select
          className={formField}
          onSelect={handleShopSelect}
          options={shopDropdownList}
          placeholder={t('shop', { defaultValue: 'Магазин', ns: 'consultation-form' }) || 'Магазин'}
          value={formData.storeId}
          warning={Boolean(formErrors.storeId)}
        />
      )}

      {isCommentVisible && (
        <Input
          className={formField}
          label={commentLabel || defaultCommentLabel}
          name="comment"
          onChange={handleFormChange}
          onFocus={handleFieldFocus}
          placeholder={commentLabel || defaultCommentLabel}
          ref={nameRef}
          value={formData.comment}
        />
      )}

      <Button className={sendButton} disabled={isLeadRequestSent} onClick={handleFormSubmit}>
        {t('button', { defaultValue: 'Отправить', ns: 'consultation-form' })}
      </Button>

      <Typography color="text-minor" variant="caption" className={captionText}>
        <Trans
          i18nKey={`disclaimer.${formType}`}
          ns="consultation-form"
          defaults="Нажимая кнопку, я даю <agreement>согласие</agreement> на обработку персональных данных, в соответствии с <policy>Политикой</policy>, и соглашаюсь с <rules>Правилами</rules>"
          components={{
            agreement: <LinkWithGAClickButton
              className={linkStyle}
              elementBlock={elementBlock}
              elementName="Согласие на обработку персональных данных"
              elementText="согласие"
              href="/personal-data-docs/"
              title="Согласие на обработку персональных данных" />,
            policy: <LinkWithGAClickButton
              className={linkStyle}
              elementBlock={elementBlock}
              elementName="Политика обработки персональных данных в ООО «ЛЕ МОНЛИД»"
              elementText="Политикой"
              href="/politika-obrabotki-personalnykh-dannykh/"
              title="Политика обработки персональных данных в ООО «ЛЕ МОНЛИД»" />,
            rules: <LinkWithGAClickButton
              className={linkStyle}
              elementBlock={elementBlock}
              elementName={rules.title}
              elementText="Правила"
              href={rules.link}
              title={rules.title} />,
          }}
        />
      </Typography>
    </form>
  );
};
